// src/App.tsx - OPTIMIZED WITH LAZY LOADING
import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ThemeProvider } from "./hooks/useTheme";
import { AnimatePresence } from "framer-motion";
import { LoadingState } from "./components/shared/LoadingState";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import { PageTransition } from "./components/shared/AnimatedSection";
import { usePageTracking } from "./hooks/useAnalytics";
import { useThemeTokens } from "./hooks/useThemeTokens";
import "./styles/index.css";

// Lazy load routes
const Portfolio = lazy(() => import("./components/Portfolio/Portfolio"));
const AllProjectsPage = lazy(() => import("./components/Portfolio/AllProjectsPage"));
const ProjectDetailPage = lazy(() => import("./components/Portfolio/ProjectDetailPage"));
const AllServicesPage = lazy(() => import("./components/Portfolio/AllServicesPage"));
const ServiceDetailPage = lazy(() => import("./components/Portfolio/ServiceDetailPage"));
const ContactPage = lazy(() => import("./components/Portfolio/ContactPage"));
const AllTestimonialsPage = lazy(() => import("./components/Portfolio/AllTestimonialsPage"));
const ReviewPage = lazy(() => import("./components/Portfolio/ReviewPage"));
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./components/Admin/AdminLogin"));
const NotFound = lazy(() => import("./components/Portfolio/NotFound"));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingState fullScreen message="Checking authentication..." />;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL;
  if (adminEmail && user.email !== adminEmail) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const NavigationWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Admin-editable palette, applied over the shipped CSS defaults
  useThemeTokens();

  // Track page views for analytics
  usePageTracking();

  useEffect(() => {
    sessionStorage.setItem("previousPath", location.pathname);

    if (location.state && (location.state as any).scrollTo) {
      const section = document.getElementById((location.state as any).scrollTo);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <NavigationWrapper>
        <ErrorBoundary>
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingState fullScreen message="Loading..." />} key={location.pathname}>
              <Routes location={location}>
                <Route path="/" element={<PageTransition><Portfolio /></PageTransition>} />
                <Route path="/projects" element={<PageTransition><AllProjectsPage /></PageTransition>} />
                <Route path="/projects/:id" element={<PageTransition><ProjectDetailPage /></PageTransition>} />
                <Route path="/services" element={<PageTransition><AllServicesPage /></PageTransition>} />
                <Route path="/services/:id" element={<PageTransition><ServiceDetailPage /></PageTransition>} />
                <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
                <Route path="/testimonials" element={<PageTransition><AllTestimonialsPage /></PageTransition>} />
                <Route path="/review/:token" element={<PageTransition><ReviewPage /></PageTransition>} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </ErrorBoundary>
      </NavigationWrapper>
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <AppRoutes />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;